import React from 'react';
import { X, Package, Users, Calendar, DollarSign } from 'lucide-react';
import { TrainingPackage } from '../types';

interface ViewPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  package: TrainingPackage | null;
}

const ViewPackageModal: React.FC<ViewPackageModalProps> = ({ isOpen, onClose, package: pkg }) => {
  if (!isOpen || !pkg) return null;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pricePerSession = pkg.price / pkg.sessions_count;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-gray-500">Информация о пакете</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {pkg.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Описание</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                    {pkg.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Тренировок
                      </p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {pkg.sessions_count}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Общая цена
                      </p>
                      <p className="text-2xl font-semibold text-green-600">
                        {formatPrice(pkg.price)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Активных клиентов
                      </p>
                      <p className="text-2xl font-semibold text-blue-600">
                        {pkg.active_clients_count || 0}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        За тренировку
                      </p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {formatPrice(pricePerSession)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Статус:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pkg.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {pkg.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm text-gray-500 space-y-1">
                  <div className="flex justify-between">
                    <span>Создан:</span>
                    <span>{formatDate(pkg.created_at)}</span>
                  </div>
                  {pkg.updated_at && (
                    <div className="flex justify-between">
                      <span>Обновлен:</span>
                      <span>{formatDate(pkg.updated_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPackageModal;